import { useState, useEffect } from 'react'
import { Users, Crown, ArrowUpCircle, CheckCircle, Bell, TrendingUp, Loader2, ExternalLink, AlertTriangle, Monitor, Clock, ChevronUp, ChevronDown } from 'lucide-react'
import { fetchScalePadAlerts, type ScalePadAlertData, type ScalePadAlertDevice } from '@/services/api'

interface MultiUserData {
  isMultiUser: boolean
  affectedScope: string
  score: number
  explanation: string
}

interface VipData {
  isVip: boolean
  reason: string
  score: number
  explanation: string
}

interface EscalationData {
  recommended: boolean
  score: number
  factors: string[]
}

interface ChronicData {
  score: number
  explanation: string
}

interface SalesOpportunityData {
  detected: boolean
  confidence: number
  type: string
  summary: string
  signals: string[]
}

interface SalesTicketResult {
  sys_id: string
  number: string
  url: string
  summary: string
}

interface AlertsTabProps {
  multiUserData?: MultiUserData | null
  vipData?: VipData | null
  escalationData?: EscalationData | null
  chronicData?: ChronicData | null
  salesOpportunityData?: SalesOpportunityData | null
  onCreateSalesTicket?: () => Promise<SalesTicketResult>
  analysisLoading?: boolean
  companyId?: string | null
  companyName?: string | null
  ticketSummary?: string | null
}

function SalesOpportunityCard({ data, onCreateTicket }: { data: SalesOpportunityData; onCreateTicket?: () => Promise<SalesTicketResult> }) {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [result, setResult] = useState<SalesTicketResult | null>(null)
  const [errorMsg, setErrorMsg] = useState('')
  const typeLabel = data.type?.replace(/-/g, ' ') || 'opportunity'

  const handleCreate = async () => {
    if (!onCreateTicket) return
    setStatus('loading')
    try {
      const res = await onCreateTicket()
      setResult(res)
      setStatus('success')
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Failed to create ticket')
      setStatus('error')
    }
  }

  return (
    <div className="bg-white dark:bg-slate-800 border border-emerald-200 dark:border-emerald-700 rounded-xl shadow-sm overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 bg-emerald-600">
        <TrendingUp className="h-3.5 w-3.5 text-white" />
        <h4 className="text-[11px] font-semibold text-white">Sales Opportunity Detected</h4>
      </div>
      <div className="p-3 space-y-1.5">
        <div className="flex items-center gap-1.5">
          <span className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 capitalize">
            {typeLabel}
          </span>
          <span className="text-[9px] text-gray-400 dark:text-slate-500">
            Confidence: {data.confidence}/10
          </span>
        </div>
        <p className="text-[11px] text-gray-700 dark:text-slate-300 leading-relaxed">
          {data.summary}
        </p>
        {data.signals && data.signals.length > 0 && (
          <ul className="space-y-1 mt-1">
            {data.signals.slice(0, 3).map((signal, i) => (
              <li key={i} className="text-[10px] text-gray-600 dark:text-slate-400 flex items-start gap-1.5">
                <span className="mt-1.5 w-1 h-1 bg-emerald-400 rounded-full flex-shrink-0" />
                {signal}
              </li>
            ))}
          </ul>
        )}

        {status === 'idle' && onCreateTicket && (
          <button
            onClick={handleCreate}
            className="mt-2 w-full px-3 py-1.5 text-[10px] font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors"
          >
            Create Sales Opp Ticket
          </button>
        )}
        {status === 'loading' && (
          <div className="mt-2 flex items-center justify-center gap-1.5 px-3 py-1.5 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg">
            <Loader2 className="h-3 w-3 animate-spin text-emerald-600 dark:text-emerald-400" />
            <span className="text-[10px] text-emerald-700 dark:text-emerald-300 font-medium">Creating ticket...</span>
          </div>
        )}
        {status === 'success' && result && (
          <div className="mt-2 bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-700 rounded-lg px-3 py-2 space-y-1">
            <div className="flex items-center gap-1.5">
              <CheckCircle className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
              <span className="text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">Ticket {result.number} created</span>
            </div>
            <a
              href={result.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[10px] text-emerald-600 dark:text-emerald-400 hover:text-emerald-800 dark:hover:text-emerald-200 underline font-medium"
            >
              Open in ServiceNow
              <ExternalLink className="h-2.5 w-2.5" />
            </a>
          </div>
        )}
        {status === 'error' && (
          <div className="mt-2 space-y-1.5">
            <p className="text-[10px] text-red-600 dark:text-red-400">{errorMsg}</p>
            <button
              onClick={handleCreate}
              className="w-full px-3 py-1.5 text-[10px] font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors"
            >
              Retry
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export function AlertsTab({ multiUserData, vipData, escalationData, chronicData, salesOpportunityData, onCreateSalesTicket, analysisLoading, companyId, companyName, ticketSummary }: AlertsTabProps) {
  const [equipmentAlerts, setEquipmentAlerts] = useState<ScalePadAlertData | null>(null)
  const [equipmentExpanded, setEquipmentExpanded] = useState(false)

  // Fetch ScalePad equipment alerts (contextual to ticket)
  useEffect(() => {
    if (!companyId) return
    fetchScalePadAlerts(companyId, ticketSummary || undefined)
      .then(setEquipmentAlerts)
      .catch(() => {}) // Silently fail — ScalePad may not be configured
  }, [companyId, ticketSummary])

  const alerts: { type: string; node: React.ReactNode }[] = []

  const needsEscalation = escalationData?.recommended || (escalationData?.score && escalationData.score >= 7)
  const isChronic = chronicData && chronicData.score >= 6
  const isMultiUser = multiUserData?.isMultiUser || (multiUserData?.score && multiUserData.score >= 5)
  const isVip = vipData?.isVip || false
  const hasSalesOpportunity = salesOpportunityData?.detected && salesOpportunityData.confidence >= 6

  if (needsEscalation) {
    const factors = escalationData?.factors?.slice(0, 3) || []
    alerts.push({
      type: 'escalation',
      node: (
        <div key="escalation" className="bg-white dark:bg-slate-800 border border-red-200 dark:border-red-700 rounded-xl shadow-sm overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 bg-red-500">
            <ArrowUpCircle className="h-3.5 w-3.5 text-white" />
            <h4 className="text-[11px] font-semibold text-white">Escalation Recommended</h4>
          </div>
          <div className="p-3 space-y-1.5">
            <p className="text-[11px] text-gray-700 dark:text-slate-300">This ticket may require immediate escalation.</p>
            {factors.length > 0 && (
              <ul className="space-y-1 mt-1">
                {factors.map((f, i) => (
                  <li key={i} className="text-[10px] text-gray-600 dark:text-slate-400 flex items-start gap-1.5">
                    <span className="mt-1.5 w-1 h-1 bg-red-400 rounded-full flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ),
    })
  }

  if (isChronic) {
    alerts.push({
      type: 'chronic',
      node: (
        <div key="chronic" className="bg-white dark:bg-slate-800 border border-orange-200 dark:border-orange-700 rounded-xl shadow-sm overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 bg-orange-500">
            <AlertTriangle className="h-3.5 w-3.5 text-white" />
            <h4 className="text-[11px] font-semibold text-white">Chronic Issue Detected</h4>
          </div>
          <div className="p-3">
            <p className="text-[11px] text-gray-700 dark:text-slate-300 leading-relaxed">
              {chronicData?.explanation || 'This appears to be a recurring issue that has been reported before.'}
            </p>
          </div>
        </div>
      ),
    })
  }

  if (isMultiUser) {
    alerts.push({
      type: 'multiuser',
      node: (
        <div key="multiuser" className="bg-white dark:bg-slate-800 border border-purple-200 dark:border-purple-700 rounded-xl shadow-sm overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 bg-purple-600">
            <Users className="h-3.5 w-3.5 text-white" />
            <h4 className="text-[11px] font-semibold text-white">Multi-User Event Detected</h4>
          </div>
          <div className="p-3 space-y-1.5">
            <p className="text-[11px] text-gray-700 dark:text-slate-300">
              <span className="font-medium text-purple-700 dark:text-purple-400">Scope:</span> {multiUserData?.affectedScope || 'Multiple users affected'}
            </p>
            <p className="text-[10px] text-gray-500 dark:text-slate-400 leading-relaxed">
              {multiUserData?.explanation || 'This issue may be affecting multiple users or is a systemic problem.'}
            </p>
          </div>
        </div>
      ),
    })
  }

  if (isVip) {
    alerts.push({
      type: 'vip',
      node: (
        <div key="vip" className="bg-white dark:bg-slate-800 border border-amber-200 dark:border-amber-700 rounded-xl shadow-sm overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 bg-amber-500">
            <Crown className="h-3.5 w-3.5 text-white" />
            <h4 className="text-[11px] font-semibold text-white">VIP / Executive Contact</h4>
          </div>
          <div className="p-3">
            <p className="text-[11px] text-gray-700 dark:text-slate-300 leading-relaxed">
              {vipData?.reason || 'This contact requires priority handling.'}
            </p>
          </div>
        </div>
      ),
    })
  }

  if (hasSalesOpportunity && salesOpportunityData) {
    alerts.push({
      type: 'sales',
      node: (
        <SalesOpportunityCard
          key="sales"
          data={salesOpportunityData}
          onCreateTicket={onCreateSalesTicket}
        />
      ),
    })
  }

  // Equipment Age Alert (ScalePad)
  if (equipmentAlerts?.hasAlerts) {
    const sevColors = {
      critical: { bg: 'bg-red-50 dark:bg-red-900/20', border: 'border-red-200 dark:border-red-700', text: 'text-red-700 dark:text-red-300', badge: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300' },
      high: { bg: 'bg-orange-50 dark:bg-orange-900/20', border: 'border-orange-200 dark:border-orange-700', text: 'text-orange-700 dark:text-orange-300', badge: 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300' },
      medium: { bg: 'bg-yellow-50 dark:bg-yellow-900/20', border: 'border-yellow-200 dark:border-yellow-700', text: 'text-yellow-700 dark:text-yellow-300', badge: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300' },
    }
    const c = sevColors[equipmentAlerts.severity as keyof typeof sevColors] || sevColors.medium
    const eolDevs = equipmentAlerts.devices.filter((d: ScalePadAlertDevice) => d.lifecycleStatus === 'eol')
    const expiredDevs = equipmentAlerts.devices.filter((d: ScalePadAlertDevice) => d.warrantyStatus === 'expired' && d.lifecycleStatus !== 'eol')
    const agingDevs = equipmentAlerts.devices.filter((d: ScalePadAlertDevice) => d.lifecycleStatus === 'aging' && d.warrantyStatus !== 'expired')
    const parts: string[] = []
    if (eolDevs.length > 0) parts.push(`${eolDevs.length} EOL`)
    if (expiredDevs.length > 0) parts.push(`${expiredDevs.length} warranty expired`)
    if (agingDevs.length > 0) parts.push(`${agingDevs.length} exceeding threshold`)

    alerts.push({
      type: 'equipment',
      node: (
        <div key="equipment" className={`${c.bg} border ${c.border} rounded-xl shadow-sm overflow-hidden`}>
          <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-slate-700 to-slate-800">
            <Monitor className="h-3.5 w-3.5 text-white" />
            <span className="text-[11px] font-semibold text-white flex-1">Equipment Lifecycle</span>
            <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${c.badge}`}>
              {equipmentAlerts.severity === 'critical' ? 'Critical' : equipmentAlerts.severity === 'high' ? 'Action Needed' : 'Review'}
            </span>
            <button onClick={() => setEquipmentExpanded(!equipmentExpanded)} className="text-white/70 hover:text-white">
              {equipmentExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </button>
          </div>
          <div className="px-3 py-2">
            <p className={`text-[10px] ${c.text}`}>
              {companyName || 'Client'} has {equipmentAlerts.totalRelevantDevices || equipmentAlerts.devices.length} device{(equipmentAlerts.totalRelevantDevices || equipmentAlerts.devices.length) !== 1 ? 's' : ''} needing attention{equipmentAlerts.matchedDeviceTypes?.length > 0 ? ` (${equipmentAlerts.matchedDeviceTypes.join(', ')})` : ''}: {parts.join(', ')}.
            </p>
            <p className="text-[9px] text-gray-400 dark:text-slate-500 mt-0.5">via ScalePad Lifecycle Manager</p>
            {equipmentExpanded && equipmentAlerts.devices.length > 0 && (
              <div className="mt-2 border-t border-gray-200 dark:border-slate-700 pt-2 space-y-1">
                {equipmentAlerts.devices.map((d: ScalePadAlertDevice, i: number) => (
                  <div key={i} className="flex items-center justify-between text-[10px]">
                    <div className="flex items-center gap-1.5">
                      <Monitor className="h-3 w-3 text-gray-400 dark:text-slate-500" />
                      <span className="font-medium text-gray-800 dark:text-slate-200">{d.name}</span>
                      <span className="text-gray-400 dark:text-slate-500 capitalize">{d.type}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {d.age && (
                        <span className="flex items-center gap-0.5 text-gray-500 dark:text-slate-400">
                          <Clock className="h-2.5 w-2.5" />{d.age.toFixed(1)}yr
                        </span>
                      )}
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${
                        d.lifecycleStatus === 'eol' ? 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300' :
                        d.lifecycleStatus === 'aging' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300' :
                        'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300'
                      }`}>
                        {d.lifecycleStatus === 'eol' ? 'EOL' : d.lifecycleStatus === 'aging' ? 'Aging' : 'Active'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ),
    })
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto lite-scrollbar space-y-3 p-1">
        {/* Summary header */}
        <div className="flex items-center gap-2 px-2 py-2 bg-white/80 dark:bg-slate-800/80 rounded-lg border border-gray-100 dark:border-slate-700">
          <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-rezolve-green-dark">
            <Bell className="h-3.5 w-3.5 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-gray-800 dark:text-slate-100">Ticket Alerts</h3>
            <p className="text-[10px] text-gray-400 dark:text-slate-500">
              {analysisLoading ? 'Analyzing...' : alerts.length > 0 ? `${alerts.length} alert${alerts.length !== 1 ? 's' : ''} detected` : 'All clear'}
            </p>
          </div>
          {alerts.length > 0 && (
            <span className="px-2 py-0.5 text-[10px] font-bold bg-red-500 text-white rounded-full min-w-[22px] text-center">
              {alerts.length}
            </span>
          )}
        </div>

        {analysisLoading ? (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <Loader2 className="h-6 w-6 animate-spin text-rezolve-green" />
            <p className="text-[10px] text-gray-400 dark:text-slate-500">Analyzing ticket for alerts...</p>
          </div>
        ) : alerts.length > 0 ? (
          alerts.map((a) => a.node)
        ) : (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <div className="flex items-center justify-center w-14 h-14 rounded-full bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-100 dark:border-emerald-800">
              <CheckCircle className="h-7 w-7 text-emerald-400" />
            </div>
            <div className="text-center">
              <h4 className="text-sm font-medium text-gray-700 dark:text-slate-300">No Alerts</h4>
              <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-0.5">
                No issues detected for this ticket
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
